import { type UserData } from '@common/types/api/auth';
import { type Address, type InstallationProfile } from '@common/types/api/common';
import { type ContractsResponse } from '@common/types/api/firmware';
import { type DeviceId, type GUID, type InstallationId } from '@common/types/brandedTypes';
import { type CountryCode } from '@common/utils/device/countryCode';

export interface LocationData {
  city: string;
  zip: string;
  countryCode: CountryCode;
}

interface InstallationFirmware {
  normalizedFirmwareVersion: string;
  normalizedCommunicationFirmwareVersion?: string;
  firmwareVersionUtc?: string;
  communicationFirmwareVersionUtc?: string;
}

interface ContractType {
  id: number;
  name: string;
}

export enum ContentRestriction {
  None = 0,
  Partial = 1,
}

interface RegisterValueResponse {
  registerIndex: number;
  displayValue: number;
}

export interface InstallationComment {
  comment: string;
  installationId: number;
}

export interface InstallationInfo {
  cascade?: {
    count: number;
  };
  contentRestriction: ContentRestriction;
  id: InstallationId;
  isOnline: boolean;
  lastOnlineUtc?: string;
  createdUtc?: string;
  name: string;
  profile: InstallationProfile;
  status: {
    activeAlarms: number;
    activeCriticalAlarms: number;
  };
  location: LocationData;
  activeContract?: ContractsResponse;
  owner?: UserData;
  timeZone: string;
  coordinates?: { latitude: number; longitude: number; };
  deviceType: string;
  deviceExtension: string;
  controllerUnit: string;
  deviceOperationMode?: string;
  firmware?: InstallationFirmware;
  macAddress?: string;
  contractType?: ContractType;
  registerValues?: RegisterValueResponse[];
  pendingDeviceDeletion?: {
    createdUtc: string;
    scheduledUtc: string;
    createdByUser: {
      email: string;
    };
    isForceDeletion?: boolean;
  };
}

interface Network {
  macAddress?: string;
  localIpAddress?: string;
}

export interface InstallationFull {
  id: InstallationId;
  pid: number;
  deviceId: DeviceId;
  isOnline: boolean;
  lastOnlineUtc?: string;
  name: string;
  network: Network;
  createdUtc: string;
  pairedUtc?: string;
  installationProfileId: number;
  isAlarmDeactivationByUserEnabled?: boolean;
  address: Address;
  ownerId?: GUID;
  comment?: string;
  timeZoneId: string;
  firmware?: InstallationFirmware;
  contractType?: ContractType;
  retailerIds?: string[];
  model?: string;
  operationManualUrl?: string;
  cloudToken?: string;
}

export interface InstallationsInfoQuery {
  limit?: number;
  offset?: number;
  offlines?: boolean;
  onlines?: boolean;
  hasPendingRetailerAccess?: boolean;
  hasPendingDeviceDeletion?: boolean;
  hasEvents?: boolean;
  eventSeverity?: (-10 | 0 | 10 | 20 | 30 | 40)[];
  installationIds?: number[];
  installationProfileIds?: number[];
  contractTypeIds?: number[];
  city?: string[];
  cityExact?: boolean;
  sort?: string;
  filter?: string;
  country?: string[];
  firmwareVersion?: string[];
  communicationFirmwareVersion?: string[];
  retailerIds?: string[];
  zips?: string[];
  registerIndexes?: number[];
  valueFilter?: {
    type: string;
    definitions: string[];
  }[];
  userId?: string;
}

export interface InstallationUpdateRequest {
  id: InstallationId;
  name: string;
  address: Address;
  model?: string;
  operationManualUrl?: string;
  timeZoneId?: string;
  retailerIds?: string[];
  installationProfileId?: number;
}

interface InstallationAddressResponse {
  country?: string;
  city?: string;
  zip?: string;
}

export interface InstallationInfoByMacResponse {
  id: InstallationId;
  name?: string;
  controlUnit?: string;
  deviceType: string;
  address: InstallationAddressResponse;
}

export type GatewayType = 'Isg' | 'Wss';

export interface PendingDeviceResponse {
  id: GUID;
  gatewayType: GatewayType;
  cloudToken: string;
  serial: string;
  deviceType: string;
  user: {
    email: string;
    firstName: string;
    lastName: string;
  };
  address: any;
  createdUtc: string;
  deviceConnectedUtc: string;
  deviceId: DeviceId;
}

export interface CreatePendingDeviceRequestBase {
  firstName: string;
  lastName: string;
  street: string;
  houseNumber: string;
  city: string;
  postalCode: string;
  countryCode: string;
  emailAddress: string;
  phone?: string;
}

export interface CreatePendingDeviceResponse {
  id: GUID;
}

export interface PendingDeviceRegistration {
  deviceIdentifier: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phone: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  /** Prefixed filter value from the country dropdown, e.g. `country-DE`. */
  countryCode: string;
}

export enum DeviceSnapshotRequestType {
  SendConfig = 0,
}

export interface ConnectionHistoryResponse {
  durationInMinutes: number;
  changedUtc: string;
  wasOnline: boolean;
}
